import {
  AfterViewInit,
  computed,
  Directive,
  effect,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  Renderer2,
  signal,
} from '@angular/core';
import { UserFacadeService } from '../../../store/user/user-facade.service';

/**
 * Examples of appReadOnlyHide use cases, input values and their transformations:
 *
 * <div [appReadOnlyHide]="<boolean>"> - value is boolean      - keep as is
 * <div appReadOnlyHide>               - value is empty string - transform to true
 * <div appReadOnly>                   - value is undefined    - transform to false
 */
function appReadOnlyHideTransform(v: boolean | '' | undefined): boolean {
  return v === true || v === '' ? true : false;
}

@Directive({
  selector: '[appReadOnly], [appReadOnlyHide]',
  standalone: true,
})
export class ReadOnlyDirective implements OnDestroy, AfterViewInit {
  @Input({ transform: appReadOnlyHideTransform }) appReadOnlyHide: boolean = false;
  @Input() ignoreReadOnly: boolean = false;
  @Input() set disabled(value: boolean) {
    this._disabled.set(!!value);
  }
  @Input() set isLoading(value: boolean) {
    this._isLoading.set(!!value);
  }

  private _elementRef = inject(ElementRef);
  private _userFacadeService = inject(UserFacadeService);
  private _renderer = inject(Renderer2);

  private _disabled = signal<boolean>(false);
  private _isDisabled = computed(() => {
    return this._disabled() || this._isLoading() || (!this.ignoreReadOnly && this._userFacadeService.isReadOnly());
  });
  private _isLoading = signal<boolean>(false);
  private _targetElement = signal<HTMLElement | null>(null);

  private _effectRef = effect(() => {
    const targetElement = this._targetElement();
    if (targetElement) {
      // Hide the element if the appReadOnlyHide is true and the user is read-only
      const hide = this.appReadOnlyHide && this._userFacadeService.isReadOnly();
      if (hide) {
        targetElement.remove();
        return;
      }

      // Set or remove the 'disabled' attribute
      const disabled = this._isDisabled();
      if (disabled) {
        this._renderer.setAttribute(targetElement, 'disabled', 'true');
        this._renderer.setStyle(targetElement, 'cursor', 'not-allowed');
      } else {
        this._renderer.removeAttribute(targetElement, 'disabled');
        this._renderer.removeStyle(targetElement, 'cursor');
      }

      // Add or remove the 'data-ignore-readonly' attribute
      // Used for Playwright tests to ignore the read-only state
      if (this.ignoreReadOnly) {
        this._renderer.setAttribute(targetElement, 'data-ignore-readonly', 'true');
      } else {
        this._renderer.removeAttribute(targetElement, 'data-ignore-readonly');
      }
    }
  });

  ngAfterViewInit() {
    const targetElement: HTMLElement = this._elementRef.nativeElement;
    const isPrimeNGComponent = targetElement.tagName.startsWith('P-');

    if (isPrimeNGComponent) {
      this._handlePrimeNgComponent(targetElement);
    } else {
      this._targetElement.set(targetElement);
    }
  }

  ngOnDestroy() {
    if (this._effectRef) {
      this._effectRef.destroy();
    }
  }

  private _handlePrimeNgComponent(targetElement: HTMLElement) {
    switch (targetElement.tagName) {
      case 'P-CALENDAR':
        const innerElement = targetElement.querySelector('input');
        this._targetElement.set(innerElement);
        break;

      case 'P-INPUTSWITCH': {
        if (this._isDisabled()) {
          const pSwitch = targetElement.querySelector('.p-inputswitch');
          pSwitch?.classList.add('p-disabled');
          targetElement.style.cursor = 'not-allowed';
        }
        break;
      }

      case 'P-MULTISELECT': {
        if (this._isDisabled()) {
          const pMultiSelect = targetElement.querySelector('.p-multiselect');
          pMultiSelect?.classList.add('p-disabled');
          targetElement.style.cursor = 'not-allowed';
          const innerInputElement = targetElement.querySelector('input');
          this._targetElement.set(innerInputElement);
        }
        break;
      }

      case 'P-SLIDER': {
        if (this._isDisabled()) {
          const pSlider = targetElement.querySelector('.p-slider');
          pSlider?.classList.add('p-disabled');
        }
        break;
      }
      case 'P-DROPDOWN': {
        if (this._isDisabled()) {
          const pDropdown = targetElement.querySelector('.p-dropdown');
          pDropdown?.classList.add('p-disabled');
        }
        break;
      }
    }
  }
}
